import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectInput, MultiSelectInput, Button } from '../../../components';

const T_PREFIX = 'lastmile_management.dispatch_schedules';

const ALL_OPTION_ID = '__all__';

export const DispatchScheduleFilters = ({
    carrierNames = [],
    channelTypes = [],
    availableStates = [],
    shippingMethods = [],
    filters,
    onFiltersChange,
    onReset
}) => {
    const { i18n } = useTranslation();

    const carrierOptions = useMemo(() => [
        { id: ALL_OPTION_ID, name: i18n.t(`${T_PREFIX}.grouped_view.all_carriers`) },
        ...carrierNames.map(name => ({ id: name, name }))
    ], [carrierNames, i18n]);

    const channelOptions = useMemo(() => [
        { id: ALL_OPTION_ID, name: i18n.t(`${T_PREFIX}.grouped_view.all_channels`) },
        ...channelTypes.map(ct => ({ id: ct.id, name: ct.name }))
    ], [channelTypes, i18n]);

    const shippingMethodOptions = useMemo(() => [
        { id: ALL_OPTION_ID, name: i18n.t(`${T_PREFIX}.grouped_view.all_shipping_methods`) },
        ...shippingMethods.map(sm => ({ id: sm.id, name: `${sm.shipping_name}${sm.carrier_name ? ` (${sm.carrier_name})` : ''}` }))
    ], [shippingMethods, i18n]);

    const stateOptions = useMemo(() =>
        availableStates.map(s => ({ id: s.code, name: `${s.code} - ${s.name}` })),
    [availableStates]);

    const enableOptions = useMemo(() => [
        { id: ALL_OPTION_ID, name: i18n.t(`${T_PREFIX}.grouped_view.all_status`) },
        { id: 'true', name: i18n.t(`${T_PREFIX}.grouped_view.enabled_only`) },
        { id: 'false', name: i18n.t(`${T_PREFIX}.grouped_view.disabled_only`) }
    ], [i18n]);

    const selectedCarrier = carrierOptions.find(o => o.id === filters.carrier_name) || carrierOptions[0];
    const selectedChannel = channelOptions.find(o => o.id === filters.channel_type_id) || channelOptions[0];
    const selectedShippingMethod = shippingMethodOptions.find(o => o.id === filters.shipping_method_id) || shippingMethodOptions[0];
    const selectedStates = useMemo(() => {
        if (!filters.shipping_state || filters.shipping_state.length === 0) return [];
        return stateOptions.filter(o => filters.shipping_state.includes(o.id));
    }, [filters.shipping_state, stateOptions]);
    const selectedEnable = enableOptions.find(o => o.id === filters.enable) || enableOptions[0];

    const handleChange = (key, value) => {
        const val = value.id === ALL_OPTION_ID ? null : value.id;
        onFiltersChange({ ...filters, [key]: val });
    };

    const handleStatesChange = (selectedOptions) => {
        const val = selectedOptions.length === 0 ? null : selectedOptions.map(o => o.id).sort();
        onFiltersChange({ ...filters, shipping_state: val });
    };

    const hasActiveFilters = filters.carrier_name || filters.channel_type_id || filters.shipping_method_id || (filters.shipping_state && filters.shipping_state.length > 0) || filters.enable;

    return (
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 mb-4">
            <div className="flex flex-wrap items-end gap-3">
                <div className="w-48">
                    <SelectInput
                        label={i18n.t(`${T_PREFIX}.filters.carrier_name`)}
                        options={carrierOptions}
                        value={selectedCarrier}
                        onChange={(val) => handleChange('carrier_name', val)}
                        buttonClassName={filters.carrier_name ? 'border-indigo-400 border-2' : ''}
                    />
                </div>
                <div className="w-48">
                    <SelectInput
                        label={i18n.t(`${T_PREFIX}.filters.channel_type_id`)}
                        options={channelOptions}
                        value={selectedChannel}
                        onChange={(val) => handleChange('channel_type_id', val)}
                        buttonClassName={filters.channel_type_id ? 'border-indigo-400 border-2' : ''}
                    />
                </div>
                {shippingMethods.length > 0 && (
                    <div className="w-52">
                        <SelectInput
                            label={i18n.t(`${T_PREFIX}.filters.shipping_method_id`)}
                            options={shippingMethodOptions}
                            value={selectedShippingMethod}
                            onChange={(val) => handleChange('shipping_method_id', val)}
                            buttonClassName={filters.shipping_method_id ? 'border-indigo-400 border-2' : ''}
                        />
                    </div>
                )}
                {availableStates.length > 0 && (
                    <div className="w-52">
                        <MultiSelectInput
                            label={i18n.t(`${T_PREFIX}.filters.shipping_state`)}
                            options={stateOptions}
                            value={selectedStates}
                            placeholder={i18n.t(`${T_PREFIX}.grouped_view.all_states`)}
                            onChange={handleStatesChange}
                            buttonClassName={filters.shipping_state && filters.shipping_state.length > 0 ? 'border-indigo-400 border-2' : ''}
                        />
                    </div>
                )}
                <div className="w-44">
                    <SelectInput
                        label={i18n.t(`${T_PREFIX}.headers.enable`)}
                        options={enableOptions}
                        value={selectedEnable}
                        onChange={(val) => handleChange('enable', val)}
                        buttonClassName={filters.enable ? 'border-indigo-400 border-2' : ''}
                    />
                </div>
                {hasActiveFilters && (
                    <Button
                        type="secondary"
                        onClick={onReset}
                        className="px-3 py-1.5 text-sm"
                    >
                        {i18n.t(`${T_PREFIX}.grouped_view.reset_filters`)}
                    </Button>
                )}
            </div>
        </div>
    );
};
